import entryMixin from '../../assets/mixins/entry';
import orderCommonMixin from '../../assets/mixins/order/order-common';
import langs from '../../assets/locale/lang/order';
import throttle from 'lodash-es/throttle';

export default {
  langs,

  mixins: [entryMixin, orderCommonMixin],

  props: {
    orderSdk: {
      type: Object,
    }
  },

  data() {
    return {
      /** 当前激活的标签 */
      activeTabIndex: 0,
      /** 下滑线样式 */
      underLineStyle: '',
      /** 订单列表数据 */
      listData: {
        contents: [],
        pageNumber: 1,
        totalPages: 0
      },
      /** 正在加载列表数据 */
      isLoadingMore: false,
      /** 记录的滚动顶部距离 */
      recordScrollTop: 0,
      /** 滚动位置: 顶部 top | 底部 bottom  */
      scrollLocation: '',
      /** 向下加载没有更多页数了 */
      isNoMorePage: false,
    };
  },

  computed: {
    /** 标签数组 */
    tabTypes() {
      if (!this.orderSdk) return [];
      const { ORDER_STATUS } = this.orderSdk;
      return [
        'all',
        ...Object.keys(ORDER_STATUS).map(item => { return ORDER_STATUS[item]; }).filter(item => item !== 'close'),
      ];
    },

    showTopLoadMore() {
      return this.isLoadingMore && this.scrollLocation === 'top';
    },

    showBottomLoadMore() {
      return this.isLoadingMore && this.scrollLocation === 'bottom';
    },
  },

  watch: {
    orderSdk() {
      this.getOrderList();
    },

    isLoadingMore(val) {
      if (!val) this.scrollLocation = '';
    },

    activeTabIndex: {
      handler() {
        this.$nextTick(() => {
          this.calculateOffset();
        });
      },
      immediate: true,
    }
  },

  mounted() {
    this.calculateOffset();
    this.getOrderList();
  },

  updated() {
    this.calculateOffset();
  },

  methods: {
    _getCoreSdk() {
      return this.orderSdk;
    },

    /**
     * 初始化
     * @param {*} forceActiveIndex 指定初始化后切换显示的标签下标。
     */
    init(forceActiveIndex) {
      this.listData.contents = [];
      this.listData.pageNumber = 1;
      if (this.$refs.orderList) {
        this.$refs.orderList.scrollTop = 0;
      }
      this.$nextTick(() => {
        if (forceActiveIndex || forceActiveIndex === 0) {
          this.activeTabIndex = forceActiveIndex;
        }
        this.getOrderList();
      });
    },

    /** 计算下划线居中样式 */
    calculateOffset() {
      const activeItem = document.querySelector('.plv-iar-order-list__wrap__tabs__item--active');
      if (!activeItem) return 0;
      const activeItemLeft = activeItem.offsetLeft;
      const activeItemClientWidth = activeItem.clientWidth;
      let translateX = activeItemLeft + (activeItemClientWidth - 16) / 2 - 16;
      if (activeItemLeft === 0 && activeItemClientWidth === 0) {
        translateX = this._useLang === 'en' ? 0 : 6;
      }
      this.underLineStyle = `transform: translateX(${translateX}px);`;
    },

    /** 是否待支付状态 */
    isWaitPay(status) {
      const { ORDER_STATUS } = this.orderSdk;
      return status === ORDER_STATUS.WAIT_PAY;
    },

    /** 根据状态加载订单列表 */
    async getOrderList() {
      if (!this.orderSdk) return;

      this.isLoadingMore = true;

      const data = await this.getOrderListPureData();

      const originContents = [...this.listData.contents];

      this.listData = {
        ...data,
        contents: [
          ...originContents,
          ...data.contents
        ]
      };

      this.isLoadingMore = false;
    },

    /**
     * 仅加载列表数据，不更新视图。用作向上滑动判断是否有新数据再更新视图
     */
    getOrderListPureData(forcePageNumber = undefined) {
      const { activeTabIndex, tabTypes, listData } = this;
      return this.orderSdk.getOrderList({
        orderStatus: activeTabIndex === 0 ? undefined : tabTypes[activeTabIndex],
        pageNumber: forcePageNumber || listData.pageNumber
      });
    },

    /** 滚动加载 */
    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      /** 滚动到底部 */
      if (scrollTop + clientHeight >= scrollHeight - 10 && !this.isLoadingMore) {
        this.handleLoadMoreNext();
      }

      /** 滚动到顶部 */
      if (scrollTop < 10 && this.recordScrollTop > 10 && !this.isLoadingMore) {
        this.handleLoadMorePre();
      }
      this.recordScrollTop = scrollTop;
    },

    handleLoadMoreNext() {
      this.scrollLocation = 'bottom';
      this.isLoadingMore = true;
      const newestPageNumber = this.listData.pageNumber + 1;
      if (newestPageNumber > this.listData.totalPages) {
        this.isNoMorePage = true;
        return;
      }
      this.isNoMorePage = false;
      this.listData.pageNumber = newestPageNumber;
      this.getOrderList();
    },

    handleLoadMorePre: throttle(async function() {
      this.scrollLocation = 'top';
      this.isLoadingMore = true;
      const data = await this.getOrderListPureData(1);
      this.isLoadingMore = false;

      // 上滑跟新数据条件: 新增订单
      const rule1 = data?.contents?.[0]?.orderNo === this.listData?.contents?.[0]?.orderNo;

      if (rule1) {
        return;
      }
      this.listData = data;
    }, 1000),

    /** 未支付、待收货 订单状态&按钮均标红 */
    isNeedNoticed(status) {
      const { ORDER_STATUS } = this.orderSdk;
      return status === ORDER_STATUS.WAIT_PAY || status === ORDER_STATUS.DELIVERING;
    },

    /**
     * 是否待收货，仅订单状态标红
     */
    statusInNotice(status) {
      const { ORDER_STATUS } = this.orderSdk;
      return status === ORDER_STATUS.WAIT_DELIVERY;
    },

    /** 菜单栏切换 */
    tabChanged(e) {
      const { index } = e.target.dataset;
      const rule1 = index || index === 0;
      const rule2 = this.activeTabIndex !== Number(index);
      if (rule1 && rule2) {
        this.activeTabIndex = Number(index);
        this.init();
      }
    },

    /** 点击订单tab进入详情页 */
    toShowDetail(orderNo) {
      this.$emit('reach-detail', orderNo);
    },

    /** 订单tab按钮点击处理 */
    handleOrderBtnClick(status, orderNo) {
      const { ORDER_STATUS } = this.orderSdk;
      if (status === ORDER_STATUS.DELIVERING) {
        this.handleConfirmReceipt(orderNo);
      } else if (status === ORDER_STATUS.WAIT_PAY) {
        this.handleConfirmPay(orderNo);
      } else {
        this.toShowDetail(orderNo);
      }
    },

    /** 确认收货 */
    async handleConfirmReceipt(orderNo) {
      const isConfirm = await this.toConfirmReceipt(orderNo);

      if (!isConfirm) {
        return;
      }

      this.updateSomeOrderlDetail(orderNo);
    },

    /** 重新发起支付 */
    async handleConfirmPay(orderNo) {
      const hasPay = await this.toConfirmPay(orderNo);
      if (!hasPay) {
        return;
      }
      this.updateSomeOrderlDetail(orderNo);
    },

    async updateSomeOrderlDetail(orderNo) {
      const index = this.listData.contents.findIndex(item => item.orderNo === orderNo);

      if (this.activeTabIndex !== 0 && index > -1) {
        this.listData.contents.splice(index, 1);
        return;
      }
      const data = await this.updateDetail(orderNo);
      if (index > -1) this.$set(this.listData.contents, index, data);
    }
  }
};
